import { useState } from "react";
import { Badge, Button, Card, Collapse, Group, NumberInput, SegmentedControl, Skeleton, Stack, Switch, Text, TextInput } from "@mantine/core";
import { IconAdjustments, IconAdjustmentsHorizontal, IconSearch } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import type { OutgoingSearchResult } from "@passio/api-client";
import { passio } from "../lib/clients";
import { openClientSettings } from "../lib/clientSettings";
import { PageHeader } from "../theme/components";
import { EmptyState, ErrorNotice, RequireFeature, ResultHeader } from "../components/common";
import { FoodDetailDrawer, FoodRow } from "../components/food";
import { FeedbackButtons } from "../components/FeedbackButtons";
import { RouteModelPicker } from "../components/ModelPicker";

const EXAMPLES = ["Pad Thai", "Chobani Greek Yogurt", "high protein breakfast", "grilled chicken caesar salad", "oat milk latte"];

interface Params {
  term: string;
  mode: "semantic" | "advanced";
  limit: number;
  alternatesOnly: boolean;
  enrich: boolean;
  preferCatalogHash: string;
  nutrientIds: string;
}

const DEFAULT_LIMIT = 25;

const parseIds = (s: string) => {
  const ids = s
    .split(/[\s,]+/)
    .map(Number)
    .filter((n) => Number.isInteger(n) && n > 0);
  return ids.length ? ids : undefined;
};

export function SearchPage() {
  const [p, setP] = useState<Params>({ term: "", mode: "semantic", limit: DEFAULT_LIMIT, alternatesOnly: false, enrich: false, preferCatalogHash: "", nutrientIds: "" });
  const [showOpts, setShowOpts] = useState(false);
  const [selected, setSelected] = useState<OutgoingSearchResult | null>(null);

  const search = useMutation({
    mutationFn: (q: Params) =>
      q.mode === "semantic"
        ? passio.foods.semanticSearch(q.term, { limit: q.limit })
        : passio.foods.advancedSearch({ term: q.term, limit: q.limit, alternatesOnly: q.alternatesOnly || undefined, enrich: q.enrich || undefined, nutrientId: q.enrich ? parseIds(q.nutrientIds) : undefined, preferCatalogHash: q.preferCatalogHash || undefined }),
  });
  const run = (term = p.term) => {
    if (!term.trim()) return;
    const next = { ...p, term };
    setP(next);
    search.mutate(next);
  };

  const results = search.data?.data.results ?? [];
  const alternates = search.data?.data.alternateNames ?? [];

  return (
    <RequireFeature feature="search">
      <PageHeader
        eyebrow="GET /napi/food/search/semantic · /advanced"
        title="Food search"
        description="Natural-language and branded search across Passio's database (plus Edamam when enabled in client settings). Click a result to hydrate full nutrition by refCode."
        actions={<RouteModelPicker routes={["semanticSearch"]} />}
      />
      <Card mb="lg">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            run();
          }}
        >
          <Stack gap="sm">
            <Group gap="sm" wrap="nowrap" align="flex-end">
              <TextInput
                style={{ flex: 1 }}
                size="md"
                placeholder="Search foods, brands or describe a dish…"
                leftSection={<IconSearch size={18} />}
                value={p.term}
                onChange={(e) => setP({ ...p, term: e.currentTarget.value })}
                autoFocus
              />
              <Button type="submit" size="md" loading={search.isPending}>
                Search
              </Button>
            </Group>
            <Group justify="space-between" wrap="wrap">
              <Group gap="xs">
                <SegmentedControl
                  size="xs"
                  value={p.mode}
                  onChange={(v) => setP({ ...p, mode: v as Params["mode"] })}
                  data={[
                    { value: "semantic", label: "Semantic" },
                    { value: "advanced", label: "Advanced" },
                  ]}
                />
                <Button size="xs" variant="subtle" color="gray" leftSection={<IconAdjustments size={14} />} onClick={() => setShowOpts((o) => !o)}>
                  Options
                </Button>
              </Group>
              <Group gap={6}>
                {EXAMPLES.map((ex) => (
                  <Badge key={ex} variant="outline" color="gray" tt="none" style={{ cursor: "pointer" }} onClick={() => run(ex)}>
                    {ex}
                  </Badge>
                ))}
              </Group>
            </Group>
            <Collapse in={showOpts}>
              <Stack gap="sm" pt="xs">
                <Group gap="lg" align="flex-end" wrap="wrap">
                  <NumberInput label="limit" value={p.limit} onChange={(v) => setP({ ...p, limit: Number(v) || DEFAULT_LIMIT })} min={1} max={200} w={100} size="xs" />
                  {p.mode === "advanced" && (
                    <>
                      <Switch label="alternatesOnly" checked={p.alternatesOnly} onChange={(e) => setP({ ...p, alternatesOnly: e.currentTarget.checked })} />
                      <Switch label="enrich (ingredients + nutrients)" checked={p.enrich} onChange={(e) => setP({ ...p, enrich: e.currentTarget.checked })} />
                      {p.enrich && (
                        <TextInput label="nutrientId" description="Comma-separated; blank = keto/UPF set" placeholder="e.g. 1, 2" value={p.nutrientIds} onChange={(e) => setP({ ...p, nutrientIds: e.currentTarget.value })} size="xs" w={190} />
                      )}
                      <TextInput label="preferCatalogHash" placeholder="self" value={p.preferCatalogHash} onChange={(e) => setP({ ...p, preferCatalogHash: e.currentTarget.value })} size="xs" w={160} />
                    </>
                  )}
                </Group>
                <Group gap="xs" wrap="nowrap" align="center">
                  <Text fz="xs" c="dimmed">
                    Data sources, type, origin, region and tag filters, language and product images are request headers, set once in Client settings and sent with every search.
                  </Text>
                  <Button size="xs" variant="light" leftSection={<IconAdjustmentsHorizontal size={14} />} onClick={openClientSettings} style={{ flexShrink: 0 }}>
                    Client settings
                  </Button>
                </Group>
              </Stack>
            </Collapse>
          </Stack>
        </form>
      </Card>

      {search.error && <ErrorNotice error={search.error} onRetry={() => run()} />}
      {search.isPending && (
        <Stack gap="xs">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} h={64} radius="md" />
          ))}
        </Stack>
      )}
      {search.data && !search.isPending && (
        <Card>
          <ResultHeader title={`“${p.term}”`} count={results.length} meta={search.data.meta} feedback={<FeedbackButtons requestId={search.data.meta.requestId} itemRef={p.term} />} />
          {alternates.length > 0 && (
            <Group gap={6} mb="sm">
              <Text fz="xs" c="dimmed">
                Related:
              </Text>
              {alternates.slice(0, 12).map((a) => (
                <Badge key={a} variant="light" color="violet" tt="none" style={{ cursor: "pointer" }} onClick={() => run(a)}>
                  {a}
                </Badge>
              ))}
            </Group>
          )}
          {results.length === 0 ? (
            <EmptyState title="No results">Try a broader term, or loosen the filters in Client settings.</EmptyState>
          ) : (
            <Stack gap={2}>
              {results.map((r, i) => (
                <FoodRow
                  key={`${r.refCode}-${i}`}
                  item={r}
                  onClick={r.refCode ? () => setSelected(r) : undefined}
                  right={
                    <Stack gap={4} align="flex-end">
                      {r.type && (
                        <Badge size="xs" variant="outline" color="gray">
                          {r.type}
                        </Badge>
                      )}
                      {r.score !== undefined && (
                        <Text fz={10} c="dimmed" ff="monospace">
                          score {r.score.toFixed(2)}
                        </Text>
                      )}
                    </Stack>
                  }
                  sub={
                    r.enrichment?.ingredients ? (
                      <Text fz="xs" c="dimmed" lineClamp={2}>
                        {r.enrichment.ingredients}
                      </Text>
                    ) : undefined
                  }
                />
              ))}
            </Stack>
          )}
        </Card>
      )}
      {!search.data && !search.isPending && !search.error && <EmptyState title="Search to get started">Pick an example above, or type anything from “Big Mac” to “something light with salmon”.</EmptyState>}

      <FoodDetailDrawer refCode={selected?.refCode} name={selected?.displayName} amount={selected} onClose={() => setSelected(null)} />
    </RequireFeature>
  );
}
